<template>
  <c-box
    v-if="order && (order.orderNumber || order._id)"
    fontSize="sm"
    :my="{ base: 'md', md: '3.5rem' }"
    :mx="{ base: '-30px', md: '0' }"
    data-cy="view_order"
    v-chakra="{
      '@media print': {
        margin: '0',
        color: 'deepBlack',
        backgroundColor: 'white',
      },
    }"
  >
    <c-flex
      :px="{ base: '15px', md: '0' }"
      flex-direction="column"
      align-items="flex-start"
      justify-content="center"
      mt="4xl"
      mb="xl"
      minHeight="16"
      v-chakra="{
        '@media print': {
          px: '0 !important',
          my: 'xs',
        },
      }"
    >
      <c-heading
        as="h1"
        size="xl"
        display="flex"
        justify-content="space-between"
        width="100%"
        color="brand.purpleDark"
        font-weight="normal"
        :data-cy="isOrderConfirmation() ? 'order-confirmation-heading' : 'order-details-heading'"
        v-chakra="{
          '@media print': {
            background: 'url(/img/iloq-partner-portal-logo-landscape-brand-blue.svg)',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right',
          },
        }"
      >
        {{
          isOrderConfirmation() ? $t("orderConfirmation.title") : $t("ordering.orderDetailsHeading")
        }}
        <c-button
          background="brand.beige"
          borderColor="brand.beige"
          ml="auto"
          data-cy="print_order"
          onclick="window.print(); return false;"
          v-chakra="{
            '@media print': {
              display: 'none !important',
            },
          }"
        >
          {{ $t("orderConfirmation.print") }}
        </c-button>
      </c-heading>
      <c-text v-if="isOrderConfirmation()" fontSize="md" mt="12" mb="3xl">{{
        $t("orderConfirmation.text")
      }}</c-text>
    </c-flex>

    <c-flex
      v-chakra="{
        '@media print': {
          display: 'flex',
          borderTop: '1px solid',
          borderColor: 'gray.300',
        },
      }"
    >
      <c-box
        :border-radius="{ md: 'base' }"
        background="white"
        :box-shadow="{ md: 'base' }"
        :px="{ base: 'md', sm: '2xl', lg: '5rem' }"
        :py="{ base: 'md', sm: '3xl', lg: '3rem' }"
        :mb="{ base: 'md', md: '3xl' }"
        width="100%"
        v-chakra="{
          '@media print': {
            px: '0',
          },
        }"
      >
        <c-box v-if="order.orderNumber || order._id">
          <c-flex flexWrap="wrap" justifyContent="space-between">
            <c-flex
              v-if="order.orderStatus !== 'DRAFT'"
              :maxWidth="{ base: '100%', sm: '33.3333333%' }"
              width="100%"
              flexDirection="column"
              v-chakra="{
                '@media print': {
                  maxWidth: '33.3333333% !important',
                },
              }"
            >
              <c-heading
                as="h4"
                :mt="{ base: 'md', sm: '0' }"
                mb="sm"
                font-size="lg"
                fontWeight="bold"
              >
                {{ $t("ordering.orderId") }}</c-heading
              >
              <c-text as="span"> {{ order.orderNumber }}</c-text>
            </c-flex>
            <c-flex
              v-if="order.orderStatus === 'DRAFT'"
              :maxWidth="{ base: '100%', sm: '33.3333333%' }"
              width="100%"
              flexDirection="column"
              v-chakra="{
                '@media print': {
                  maxWidth: '33.3333333% !important',
                },
              }"
            >
              <c-heading
                as="h4"
                :mt="{ base: 'md', sm: '0' }"
                mb="sm"
                font-size="lg"
                fontWeight="bold"
                >{{ $t("ordering.orderStatuses." + order.orderStatus) }}</c-heading
              >
            </c-flex>
            <c-flex
              flexDirection="column"
              :maxWidth="{ base: '100%', sm: '33.3333333%' }"
              width="100%"
              v-chakra="{
                '@media print': {
                  maxWidth: '33.3333333% !important',
                },
              }"
            >
              <c-heading
                as="h4"
                :mt="{ base: 'md', sm: '0' }"
                mb="sm"
                font-size="lg"
                fontWeight="bold"
              >
                {{ $t("ordering.timestamp") }}</c-heading
              >
              <c-text as="span" data-cy="order-timestamp">
                {{ order.timestamp | moment("D.M.YYYY") }}
                {{ order.timestamp | moment("HH:mm") }} &nbsp;/&nbsp;
                {{ order.orderCreatorUserName }}</c-text
              >
            </c-flex>
            <c-box
              :maxWidth="{ base: '100%', sm: '33.3333333%' }"
              width="100%"
              v-if="order.orderStatus !== 'DRAFT'"
              v-chakra="{
                '@media print': {
                  maxWidth: '33.3333333% !important',
                },
              }"
            >
              <c-heading
                as="h4"
                :mt="{ base: 'md', sm: '0' }"
                mb="sm"
                font-size="lg"
                fontWeight="bold"
              >
                {{ $t("ordering.orderStatus") }}</c-heading
              >
              <c-box>
                <c-box
                  display="inline-block"
                  verticalAlign="middle"
                  mr="10px"
                  width="25%"
                  minWidth="60px"
                  v-chakra="{
                    '.order-progress > div': {
                      backgroundColor: 'brand.purple',
                    },
                    '.SHIPPED > div': {
                      backgroundColor: 'brand.green',
                    },
                    '.CANCELLED > div': {
                      backgroundColor: 'brand.redLight',
                    },
                    '.FAIL > div': {
                      backgroundColor: 'brand.purpleLight',
                    },
                    '@media print': {
                      display: 'none',
                    },
                  }"
                >
                  <c-progress
                    :value="progressPercentageByOrderStatus[order.orderStatus]"
                    :max="100"
                    :class="'order-progress ' + order.orderStatus"
                    border-radius="full"
                    size="lg"
                    background="gray.200"
                  ></c-progress>
                </c-box>
                {{ $t("ordering.orderStatuses." + order.orderStatus) }}
              </c-box>
              <c-heading as="h4" mb="sm" font-size="lg" fontWeight="bold" display="block" mt="xl">
                {{ $t("ordering.shipmentLinks.title") }}</c-heading
              >
              <c-text as="span">
                <c-box v-if="orderingShipments.length > 0" data-cy="shipment-link-list" mt="2xs">
                  <c-flex v-for="(shipment, index) in orderingShipments" :key="index">
                    <c-link :href="shipment.url" target="_blank" display="flex" mb="2xs">
                      <c-icon name="info" size="1.25rem"></c-icon>
                      <c-text as="span" pl="2xs" pr="xs" color="brand.purple" display="block">{{
                        shipment.shipmentNumber
                      }}</c-text>
                    </c-link>
                  </c-flex>
                </c-box>
                <c-text as="span" color="gray.700" v-else>-</c-text>
              </c-text>
            </c-box>
            <c-text as="span" flexBasis="100%" :display="{ base: 'block', lg: 'none' }"></c-text>
            <c-flex
              v-if="order.orderStatus === 'DRAFT'"
              :maxWidth="{ base: '100%', sm: '33.3333333%' }"
              width="100%"
              justify-content="center"
              flexDirection="column"
              my="md"
              v-chakra="{
                '@media print': {
                  display: 'none !important',
                },
              }"
            >
              <c-flex alignItems="center">
                <c-text as="span">
                  <c-button
                    m="0 auto"
                    variation="solid"
                    maxWidth="100%"
                    background="brand.beige"
                    border-color="brand.beige"
                    fontSize="sm"
                    @click="openContinueFromDraftDialog()"
                    data-cy="continueFromDraftButton"
                  >
                    {{ $t("ordering.buttons.continueFromDraft") }}
                  </c-button>
                </c-text>
                <c-text as="span">
                  <c-link
                    href="#"
                    :data-cy="`deleteLink_${order._id}`"
                    @click="openDeleteDraftDialog()"
                  >
                    <c-icon name="trashcan" size="2rem" fill="black" color="brand.purple" />
                  </c-link>
                </c-text>
              </c-flex>
            </c-flex>
          </c-flex>
        </c-box>
      </c-box>
    </c-flex>

    <dialog-modal
      :show-dialog="showContinueFromDraftDialog"
      @close="continueFromDraftDialogHandled(false)"
      content-ref="continue-from-draft-dialog"
    >
      <template v-slot:title>
        {{ $t("ordering.buttons.continueFromDraft") }}
      </template>
      <template v-slot:content>
        <c-text>{{ $t("ordering.continueFromDraft") }}</c-text>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="continueFromDraftDialogHandled(true)"
          background="brand.beige"
          borderColor="brand.beige"
          mr="md"
        >
          {{ $t("common.yesButton") }}
        </c-button>
        <c-button @click="continueFromDraftDialogHandled(false)">
          {{ $t("common.noButton") }}
        </c-button>
      </template>
    </dialog-modal>

    <dialog-modal
      :show-dialog="showDeleteDraftDialog"
      @close="continueFromDraftDeleteHandled(false)"
      content-ref="delete-draft-dialog"
    >
      <template v-slot:title>
        {{ $t("ordering.buttons.deleteDraft") }}
      </template>
      <template v-slot:content>
        <c-text>{{ $t("ordering.ensureDeleteDraft") }}</c-text>
      </template>
      <template v-slot:actions>
        <c-button
          variation="solid"
          @click="continueFromDraftDeleteHandled(true)"
          background="brand.beige"
          borderColor="brand.beige"
          mr="md"
        >
          {{ $t("common.yesButton") }}
        </c-button>
        <c-button @click="continueFromDraftDeleteHandled(false)">
          {{ $t("common.noButton") }}
        </c-button>
      </template>
    </dialog-modal>

    <c-flex v-if="order.orderNumber || order._id">
      <ViewOrderDetailsBox :order="order" />
    </c-flex>
  </c-box>
</template>

<script lang="ts">
import Modal from "@/components/Modal.vue";
import { progressPercentageByOrderStatus } from "@/constants";
import { IOrder, IOrderingShipment, IOrderRow } from "@iloq/shared";
import moment from "moment-timezone";
import { defineComponent } from "vue";
import { mapState } from "vuex";

import { CBox, CButton, CFlex, CHeading, CIcon, CLink, CProgress, CText } from "@chakra-ui/vue";

import ViewOrderDetailsBox from "./ViewOrderDetailsBox.vue";

interface IData {
  [key: string]: any;
  orderingShipments: IOrderingShipment[];
  order: IOrder | any;
}

export default defineComponent({
  name: "ViewOrder",
  components: {
    "dialog-modal": Modal,
    ViewOrderDetailsBox,
    CHeading,
    CText,
    CButton,
    CFlex,
    CBox,
    CIcon,
    CProgress,
    CLink,
  },
  props: {
    isConfirmationForNewOrder: {
      type: String,
      required: false,
      default: "false",
    },
    id: {
      type: String,
      required: true,
    },
  },
  data(): IData {
    return {
      progressPercentageByOrderStatus,
      order: {
        deliveryDetails: {
          receiverName: "test",
          email: "",
          phoneNumber: "",
          shipToAddress: {},
        },
        rows: [],
        totalSum: {
          price: 0,
          currencyCode: "",
        },
      },
      orderingShipments: [],
      showDeleteDraftDialog: false,
      showContinueFromDraftDialog: false,
    };
  },
  computed: {
    ...mapState("partnerInfo", {
      partnerDeliveryTypes: "deliveryTypes",
    }),
  },
  mounted(): void {
    this.fetchOrder();
  },
  methods: {
    async fetchOrder(): Promise<void> {
      const orderId = this.id;
      this.order = this.$store.state.orders.orders.find(
        (element: IOrder) => element.orderNumber === orderId || element._id === orderId
      );
      if (!this.order) {
        // Happens when user comes with direct link using order number (& state is not initialized)
        this.$store.dispatch("orders/fetchOrders", { root: true });
      } else {
        await this.fetchShipments();
      }

      this.$store.watch(
        (state: any) =>
          state.orders.orders.find(
            // eslint-disable-next-line no-underscore-dangle
            (element: IOrder) => element.orderNumber === orderId || element._id === orderId
          ),
        (newValue: IOrder) => {
          if (newValue) {
            this.order = newValue;
            this.fetchShipments();
          }
        }
      );
    },

    async fetchShipments(): Promise<void> {
      // fetch only if there actually is orderNumber, skipping e.g. drafts
      if (this.order?.orderNumber) {
        this.orderingShipments = await this.$store.dispatch(
          "orders/fetchOrderingShipments",
          this.order
        );
      }
    },

    openContinueFromDraftDialog(): void {
      if (this.hasEmptyShoppingCart()) {
        this.continueFromDraft();
      } else {
        this.showContinueFromDraftDialog = true;
      }
    },

    hasEmptyShoppingCart(): boolean {
      return this.$store.state.shoppingCart.cartRows.length === 0;
    },

    continueFromDraft(): void {
      this.$store
        .dispatch("productSearch/ensureProductsExistWithLatestDataAndPrice", {
          rows: this.order.rows,
          failWhenNoProducts: true,
        })
        .then(async (orderRowsWithFreshProducts: IOrderRow[]) => {
          await this.$store.dispatch("shoppingCart/continueOrderFromDraft", {
            ...this.order,
            rows: orderRowsWithFreshProducts,
          });
          this.$router.push({ path: "/shopping_cart" });
        })
        .catch(() => {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("ordering.continueFromDraftError.title"),
            dialogText: this.$t("ordering.continueFromDraftError.text"),
          });
        });
    },

    continueFromDraftDialogHandled(success: boolean): void {
      this.showContinueFromDraftDialog = false;
      if (success) {
        this.continueFromDraft();
      }
    },

    openDeleteDraftDialog(): void {
      this.showDeleteDraftDialog = true;
    },

    continueFromDraftDeleteHandled(success: boolean): void {
      this.showDeleteDraftDialog = false;
      if (success) {
        this.deleteDraft();
      }
    },

    deleteDraft(): void {
      this.$store
        .dispatch("orders/deleteOrder", this.order)
        .then(() => {
          this.$store.dispatch("orders/fetchOrders");
          this.$router.push({ path: "/orders" });
        })
        .catch(() => {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("ordering.deleteDraftError.title"),
            dialogText: this.$t("ordering.deleteDraftError.text"),
          });
        });
    },

    isOrderConfirmation(): boolean {
      return this.isConfirmationForNewOrder === "true";
    },

    getDateInILOQLocalTime(date: Date): any {
      return moment(date).utc(false).tz("Europe/Helsinki");
    },
  },
});
</script>
